import React from 'react';
import { Switch, Typography, theme } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconShieldCheck } from '@tabler/icons-react';
import EmptyState from './EmptyState';
import { IHostInsurancePolicy } from '../interfaces/heavyDutyMachinery/IHostInsurancePolicy';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const { Text } = Typography;

interface InsurancePolicyAssignmentPickerProps {
  policies: IHostInsurancePolicy[];
  loading?: boolean;
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
}

/**
 * Multi-toggle picker for "which of my own insurance policies does THIS
 * machine offer" -- same staged Form.Item pattern as
 * AccessoryAssignmentPicker (see that component's own doc comment), no
 * category-compatibility filter since insurance coverage isn't tied to a
 * Machinery.Category (see HostInsurancePolicyLink's own doc comment, API
 * repo).
 */
const InsurancePolicyAssignmentPicker: React.FC<InsurancePolicyAssignmentPickerProps> = ({
  policies,
  loading,
  value,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const selected = new Set(value ?? []);

  const activePolicies = policies.filter((p) => p.isActive);

  const toggle = (policyId: string, checked: boolean) => {
    if (disabled) return;
    const next = new Set(selected);
    if (checked) next.add(policyId);
    else next.delete(policyId);
    onChange?.(Array.from(next));
  };

  if (loading) return null;

  if (activePolicies.length === 0) {
    return (
      <EmptyState
        icon={<IconShieldCheck size={20} />}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_LINK_EMPTY_TITLE)}
        description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_LINK_EMPTY_DESCRIPTION)}
        action={
          <Link to="/heavy-duty-machinery/insurance-policies" target="_blank" rel="noopener noreferrer">
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_LINK_EMPTY_CTA)}
          </Link>
        }
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {activePolicies.map((policy) => (
        <div
          key={policy.id}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            borderRadius: 14,
            background: token.colorBgContainer,
            border: `1px solid ${selected.has(policy.id) ? token.colorPrimary : token.colorBorderSecondary}`,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <Text strong style={{ display: 'block' }}>{policy.name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{policy.pricePerDay.toFixed(2)}€/día</Text>
          </div>
          <Switch
            checked={selected.has(policy.id)}
            disabled={disabled}
            onChange={(checked) => toggle(policy.id, checked)}
          />
        </div>
      ))}
    </div>
  );
};

export default InsurancePolicyAssignmentPicker;
